import React, { useState, useEffect, useContext, useRef } from 'react';
import { Button, TextField, DialogContent, DialogActions, Grid, Popover, useMediaQuery, InputAdornment, IconButton, Tooltip } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { REACT_APP_BACKEND_URL } from "../../config";
import PropType from 'prop-types'
import Dialog from '../Dialog';
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { i18n } from '../../translate/i18n';
import { makeStyles } from '@material-ui/core/styles';
import ButtonWithSpinner from '../ButtonWithSpinner';
import { AuthContext } from "../../context/Auth/AuthContext";
import InsertEmoticonIcon from '@material-ui/icons/InsertEmoticon';
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera';
import CloseIcon from '@material-ui/icons/Close';
import { toast } from "react-toastify";

import { isNil, isObject, has, get } from 'lodash';
import EmojiPicker from 'emoji-picker-react';

import api from '../../services/api';

const MessageSchema = Yup.object().shape({
    shortcode: Yup.string()
        .min(3, "Muy corto!")
        .max(50, "Demasiado largo!")
        .required("Requerido"),
    message: Yup.string()
        .min(3, "Muy corto!")
        .max(1000, "Demasiado largo!")
        .required("Requerido")
});


const useStyles = makeStyles((theme) => ({
    root: {
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
            width: '100%',
        },
    },
    dialogContent: {
        padding: theme.spacing(2),
    },
    textField: {
        width: '100%',
    },
    emojiButton: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
    },
    emojiPickerStyle: {
        padding: theme.spacing(1),
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            width: '100%',
        },
    },
    messageField: {
        '& .MuiInputBase-root': {
            paddingRight: 0,
        },
    },
    messageRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    messageFieldContainer: {
        flex: 1,
    },
    mediaSidebar: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    mediaButton: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main,
        color: '#FFFFFF',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    mediaButtonDisabled: {
        opacity: 0.5,
    },
    mediaPreviewWrapper: {
        position: 'relative',
        marginTop: theme.spacing(1),
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${theme.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)"}`,
        backgroundColor: theme.palette.background.default,
        maxWidth: 260,
    },
    mediaPreview: {
        display: 'block',
        width: '100%',
        maxHeight: 180,
        objectFit: 'cover',
    },
    mediaRemoveButton: {
        position: 'absolute',
        top: 6,
        right: 6,
        backgroundColor: 'rgba(0,0,0,0.55)',
        color: '#FFFFFF',
        width: 28,
        height: 28,
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.75)',
        },
    },
    mediaCaption: {
        padding: theme.spacing(0.75, 1.25),
        fontSize: 12,
        color: theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
    mediaChangeButton: {
        position: 'absolute',
        bottom: 6,
        right: 6,
        backgroundColor: 'rgba(0,0,0,0.55)',
        color: '#FFFFFF',
        width: 32,
        height: 32,
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.75)',
        },
    },
}));

function QuickMessageDialog(props) {
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const initialMessage = {
        id: null,
        shortcode: '',
        message: '',
        mediaUrl: null,
        mediaPath: null,
    };

    const { modalOpen, saveMessage, editMessage, onClose, messageSelected, loadingSave } = props;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [message, setMessage] = useState(initialMessage);
    const [loading, setLoading] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState(null);
    const messageRef = useRef(null);
    const shortcodeRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const emojiButtonRef = useRef(null);

    const { user } = useContext(AuthContext);

    useEffect(() => {
        verifyAndSetMessage()
        setDialogOpen(modalOpen)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen])

    useEffect(() => {
        verifyAndSetMessage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageSelected])

    useEffect(() => {
        return () => {
            if (mediaPreview && mediaPreview.startsWith('blob:')) {
                URL.revokeObjectURL(mediaPreview);
            }
        };
    }, [mediaPreview]);

    const messageSelectedIsValid = () => {
        return isObject(messageSelected) && has(messageSelected, 'id') && !isNil(get(messageSelected, 'id'))
    }

    const verifyAndSetMessage = () => {
        if (messageSelectedIsValid()) {
            const { id, message, shortcode, mediaUrl, mediaPath } = messageSelected
            setMessage({ id, message, shortcode, mediaUrl, mediaPath })
            const existingMedia = mediaUrl || mediaPath;
            if (existingMedia) {
                setMediaPreview(existingMedia.startsWith('http')
                    ? existingMedia
                    : `${REACT_APP_BACKEND_URL}${existingMedia}`);
                setMediaFile(null);
            } else {
                setMediaPreview(null);
                setMediaFile(null);
            }
        } else {
            setMessage(initialMessage)
            setMediaPreview(null);
            setMediaFile(null);
        }
    }

    const handleClose = () => {
        onClose()
        setLoading(false)
        if (mediaPreview && mediaPreview.startsWith('blob:')) {
            URL.revokeObjectURL(mediaPreview);
        }
        setMediaFile(null);
        setMediaPreview(null);
    }

    const handleMediaButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleMediaChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Solo se permiten imágenes');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('La imagen no puede pesar más de 5 MB');
            return;
        }
        if (mediaPreview && mediaPreview.startsWith('blob:')) {
            URL.revokeObjectURL(mediaPreview);
        }
        setMediaFile(file);
        setMediaPreview(URL.createObjectURL(file));
        event.target.value = '';
    };

    const handleRemoveMedia = () => {
        if (mediaPreview && mediaPreview.startsWith('blob:')) {
            URL.revokeObjectURL(mediaPreview);
        }
        setMediaFile(null);
        setMediaPreview(null);
        setMessage((prev) => ({ ...prev, mediaUrl: null, mediaPath: null }));
    };

    const handleSave = async (values, { setSubmitting }) => {
        setLoading(true);
        try {
            const basePayload = messageSelectedIsValid()
                ? { ...messageSelected, ...values, userId: user.id }
                : { ...values, userId: user.id };

            if (mediaFile) {
                const formData = new FormData();
                Object.entries(basePayload).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && key !== 'id') {
                        formData.append(key, value);
                    }
                });
                formData.append('media', mediaFile);

                if (messageSelectedIsValid()) {
                    await editMessage({ ...basePayload, _formData: formData });
                } else {
                    await saveMessage({ ...basePayload, _formData: formData });
                }
            } else {
                if (messageSelectedIsValid()) {
                    await editMessage({
                        ...basePayload,
                        removeMedia: !mediaPreview,
                    });
                } else {
                    await saveMessage(basePayload);
                }
            }
            handleClose();
        } catch (error) {
            console.error('Error al guardar el mensaje rápido:', error);
            toast.error('Error al guardar el mensaje rápido');
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    }


    const handleEmojiButtonClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleEmojiPickerClose = () => {
        setAnchorEl(null);
    };

    const handleEmojiClick = (emojiObject, setFieldValue) => {
        const emoji = emojiObject.emoji || emojiObject.native || emojiObject;
        const field = messageRef.current;
        const currentShortcode = shortcodeRef.current.value;

        if (field) {
            const start = field.selectionStart || 0;
            const end = field.selectionEnd || 0;
            const text = field.value || "";
            const newText = text.substring(0, start) + emoji + text.substring(end);

            setFieldValue("message", newText);
            setFieldValue("shortcode", currentShortcode);
            field.focus();
            setTimeout(() => {
                field.setSelectionRange(start + emoji.length, start + emoji.length);
            }, 0);
        }
        handleEmojiPickerClose();
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            <Dialog
                title="Mensaje Rápido"
                modalOpen={dialogOpen}
                onClose={handleClose}
                fullScreen={fullScreen}
                fullWidth
                maxWidth="sm"
            >
                <Formik
                    initialValues={message}
                    enableReinitialize
                    validationSchema={MessageSchema}
                    onSubmit={handleSave}
                >
                    {({ touched, errors, isSubmitting, setFieldValue, isValid, dirty, values }) => (
                        <Form>
                            <DialogContent className={classes.dialogContent}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <div className={classes.messageRow}>
                                            <div className={classes.messageFieldContainer}>
                                                <Field
                                                    as={TextField}
                                                    name="shortcode"
                                                    label={i18n.t("quickMessages.dialog.shortcode")}
                                                    error={touched.shortcode && Boolean(errors.shortcode)}
                                                    helperText={touched.shortcode && errors.shortcode}
                                                    variant="outlined"
                                                    inputRef={shortcodeRef}
                                                    fullWidth
                                                />
                                            </div>
                                            <div className={classes.mediaSidebar}>
                                                <Tooltip title={mediaPreview ? "Cambiar imagen" : "Agregar imagen"}>
                                                    <span>
                                                        <IconButton
                                                            className={`${classes.mediaButton} ${mediaPreview ? classes.mediaButtonDisabled : ''}`}
                                                            onClick={handleMediaButtonClick}
                                                            disabled={Boolean(mediaPreview)}
                                                            aria-label="Agregar imagen"
                                                        >
                                                            <PhotoCameraIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMediaChange}
                                            style={{ display: 'none' }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            name="message"
                                            inputRef={messageRef}
                                            rows={6}
                                            label={i18n.t("quickMessages.dialog.message")}
                                            multiline
                                            error={touched.message && Boolean(errors.message)}
                                            helperText={touched.message && errors.message}
                                            variant="outlined"
                                            fullWidth
                                            className={classes.messageField}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Button
                                                            ref={emojiButtonRef}
                                                            color="primary"
                                                            className={classes.emojiButton}
                                                            onClick={handleEmojiButtonClick}
                                                        >
                                                            <InsertEmoticonIcon style={{ color: "black" }} />
                                                        </Button>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        {mediaPreview && (
                                            <div className={classes.mediaPreviewWrapper}>
                                                <img
                                                    src={mediaPreview}
                                                    alt="Vista previa"
                                                    className={classes.mediaPreview}
                                                />
                                                <Tooltip title="Quitar imagen">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.mediaRemoveButton}
                                                        onClick={handleRemoveMedia}
                                                        aria-label="Quitar imagen"
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                {mediaFile && (
                                                    <Tooltip title="Cambiar imagen">
                                                        <IconButton
                                                            size="small"
                                                            className={classes.mediaChangeButton}
                                                            onClick={handleMediaButtonClick}
                                                            aria-label="Cambiar imagen"
                                                        >
                                                            <PhotoCameraIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <div className={classes.mediaCaption}>
                                                    {mediaFile
                                                        ? `Nueva imagen: ${mediaFile.name}`
                                                        : 'Imagen guardada'}
                                                </div>
                                            </div>
                                        )}
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="primary"
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </Button>
                                <ButtonWithSpinner
                                    loading={isSubmitting || loadingSave}
                                    color="primary"
                                    type="submit"
                                    variant="contained"
                                    autoFocus
                                    disabled={isSubmitting || !isValid || !dirty}
                                >
                                    Guardar
                                </ButtonWithSpinner>
                            </DialogActions>
                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={handleEmojiPickerClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <div className={classes.emojiPickerStyle}>
                                    <EmojiPicker
                                        onEmojiClick={(emojiObject) => handleEmojiClick(emojiObject, setFieldValue)}
                                        searchPlaceholder="Buscar emojis"
                                        categories={{
                                            smileys_people: 'Caras y personas',
                                            animals_nature: 'Animales y naturaleza',
                                            food_drink: 'Comida y bebida',
                                            travel_places: 'Viajes y lugares',
                                            activities: 'Actividades',
                                            objects: 'Objetos',
                                            symbols: 'Símbolos',
                                            flags: 'Banderas'
                                        }}
                                    />
                                </div>
                            </Popover>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </>
    )
}

QuickMessageDialog.propType = {
    modalOpen: PropType.bool,
    onClose: PropType.func
}

export default QuickMessageDialog;
